import React, { useMemo } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from '@stripe/react-stripe-js';
import {
  CancelPayment,
  CardDetails,
  CardError,
  CardHolderName,
  CardLabel,
  Cvv,
  ExpiryDate,
  FooterButtonContainer,
  PaymentForm,
} from '@app/modules/payment-due/payment-due-component';
import { Button } from '@app/components';
import { PaymentSuccess } from '@app/types';
import { currencyFormat } from '@app/utils';

const useOptions = () => {
  const options = useMemo(
    () => ({
      style: {
        base: {
          fontSize: '16px',
          color: '#424770',
          letterSpacing: '0.025em',
          fontFamily: 'Source Code Pro, monospace',
          '::placeholder': {
            color: '#aab7c4',
          },
        },
        invalid: {
          color: '#9e2146',
        },
      },
    }),
    [],
  );

  return options;
};

type PaymentProps = {
  amount: number;
  onCancel: (e: any) => void;
  transactionPayment: any;
  paymentStatus?: PaymentSuccess;
};

export default function Payment({
  amount,
  onCancel,
  transactionPayment,
}: PaymentProps) {
  const dollar: string = '$';
  const [cardHolderName, setCardHolderName] = React.useState('');
  const stripe: any = useStripe();
  const elements: any = useElements();
  const options = useOptions();
  const [paymentText, updatePaymentText] = React.useState('Pay Now');
  const [isSubmitted, updateIsSubmitted] = React.useState(false);
  const [cardError, updateCardError] = React.useState('');

  /**
   *
   * @param event to make payment
   */
  const makePayment = async (event: any) => {
    updateIsSubmitted(true);
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    const cardNumber = elements.getElement(CardNumberElement);
    if (cardNumber) {
      const payload = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber,
      });
      if (payload.error || !cardHolderName) {
        updateCardError(
          payload.error
            ? payload.error.message
            : 'Please enter card holder name',
        );
        updateIsSubmitted(false);
        updatePaymentText('Pay Now');
      } else {
        updatePaymentText('Processing...');
        const paymentStatus = await transactionPayment(payload, cardHolderName);
        if (paymentStatus) {
          updateIsSubmitted(false);
        }
      }
    }
  };

  /**
   *
   * @param event to update error message
   */
  function onCardChange(event: any, type?: string) {
    updateCardError('');

    if (type === 'holderName') {
      setCardHolderName(event.target.value);
    }
  }
  return (
    <PaymentForm>
      <CardLabel>Card number</CardLabel>
      <CardNumberElement
        options={options}
        onChange={(e: any) => onCardChange(e)}
      />
      <CardDetails>
        <ExpiryDate>
          <CardLabel>Expiration date</CardLabel>
          <CardExpiryElement
            options={options}
            onChange={(e: any) => onCardChange(e)}
          />
        </ExpiryDate>
        <Cvv>
          <CardLabel>CVC</CardLabel>
          <CardCvcElement
            options={options}
            onChange={(e: any) => onCardChange(e)}
          />
        </Cvv>
      </CardDetails>
      <CardDetails>
        <ExpiryDate>
          <CardLabel>Card Holder Name</CardLabel>
          <CardHolderName
            value={cardHolderName}
            placeholder="Enter card holder name"
            onChange={e => onCardChange(e, 'holderName')}></CardHolderName>
        </ExpiryDate>
        <Cvv>
          <CardLabel>Amount</CardLabel>
          <CardHolderName
            value={`${dollar}${currencyFormat(amount)}`}
            disabled={true}></CardHolderName>
        </Cvv>
      </CardDetails>
      <CardError>{cardError}</CardError>
      <FooterButtonContainer style={{ marginTop: '32px' }}>
        <CancelPayment onClick={onCancel}>Cancel</CancelPayment>
        <Button
          variant="warning"
          disabled={isSubmitted}
          onClick={(e: any) => makePayment(e)}>
          {paymentText}
        </Button>
      </FooterButtonContainer>
    </PaymentForm>
  );
}
